/* eslint-disable no-underscore-dangle */
import * as idb from 'lib0/indexeddb';
import { Observable } from 'lib0/observable';
import * as promise from 'lib0/promise';
import * as Y from 'yjs';

import { captureException } from '@canvas/sentry-core';

const customStoreName = 'custom';
const updatesStoreName = 'updates';

export const PREFERRED_TRIM_SIZE = 500;

export const fetchUpdates = (
  idbPersistence: IndexeddbPersistence,
  beforeApplyUpdatesCallback: (store: IDBObjectStore) => void = () => {},
  afterApplyUpdatesCallback: (store: IDBObjectStore) => void = () => {}
) => {
  // NOTE (akishore): Existing bug in the JS implementation. Will be addressed after hotfix.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const db = idbPersistence.db!;
  const [updatesStore] = idb.transact(db, [updatesStoreName]); // , 'readonly')
  return idb
    .getAll(updatesStore, idb.createIDBKeyRangeLowerBound(idbPersistence._dbref, false))
    .then((updates) => {
      if (!idbPersistence._destroyed) {
        beforeApplyUpdatesCallback(updatesStore);
        Y.transact(
          idbPersistence.doc,
          () => {
            updates.forEach((val) => Y.applyUpdate(idbPersistence.doc, val));
          },
          idbPersistence,
          false
        );
        afterApplyUpdatesCallback(updatesStore);
      }
    })
    .then(() =>
      idb.getLastKey(updatesStore).then((lastKey) => {
        idbPersistence._dbref = lastKey + 1;
      })
    )
    .then(() =>
      idb.count(updatesStore).then((cnt) => {
        idbPersistence._dbsize = cnt;
      })
    )
    .then(() => updatesStore);
};

export const storeState = (idbPersistence: IndexeddbPersistence, forceStore = true) => {
  return fetchUpdates(idbPersistence).then((updatesStore) => {
    if (forceStore || idbPersistence._dbsize >= PREFERRED_TRIM_SIZE) {
      idb
        // lib0's types are overly restrictive; IndexedDB accepts Uint8Array directly
        .addAutoKey(
          updatesStore,
          Y.encodeStateAsUpdate(
            idbPersistence.doc
          ) satisfies Uint8Array as unknown as ArrayBuffer
        )
        .then(() =>
          idb.del(
            updatesStore,
            idb.createIDBKeyRangeUpperBound(idbPersistence._dbref, true)
          )
        )
        .then(() =>
          idb.count(updatesStore).then((cnt) => {
            idbPersistence._dbsize = cnt;
          })
        );
    }
  });
};

export const clearDocument = (name: string) => idb.deleteDB(name);

export class IndexeddbPersistence extends Observable<string> {
  doc: Y.Doc;
  name: string;
  _dbref: number;
  _dbsize: number;
  _destroyed: boolean;
  db: IDBDatabase | null;
  synced: boolean;
  _db: Promise<IDBDatabase>;
  whenSynced: Promise<IndexeddbPersistence>;
  _storeTimeout: number;
  _storeTimeoutId: ReturnType<typeof setTimeout> | null;
  _storeUpdate: (update: Uint8Array, origin: any) => void;

  // This callback allows the whiteboard app to update valtio state which
  // corresponds to signalling if we can still proceed with using indexeddb.
  // This is important for covering cases where indexeddb may break _after_
  // initialisation, which is otherwise covered by `indexeddb-healthcheck.tsx`.
  _indexeddbHealthcheckCallback: (healthy: boolean) => void;

  constructor(
    name: string,
    doc: Y.Doc,
    indexeddbHealthcheckCallback: (healthy: boolean) => void
  ) {
    super();
    this.doc = doc;
    this.name = name;
    this._dbref = 0;
    this._dbsize = 0;
    this._destroyed = false;
    this.db = null;
    this.synced = false;
    this._db = idb.openDB(name, (db) =>
      idb.createStores(db, [['updates', { autoIncrement: true }], ['custom']])
    );

    this._indexeddbHealthcheckCallback = indexeddbHealthcheckCallback;

    this.whenSynced = promise.create((resolve) => this.on('synced', () => resolve(this)));

    this._db.then((db) => {
      this.db = db;

      // lib0's types are overly restrictive; IndexedDB accepts Uint8Array directly
      const beforeApplyUpdatesCallback = (updatesStore: IDBObjectStore) =>
        idb.addAutoKey(
          updatesStore,
          Y.encodeStateAsUpdate(doc) satisfies Uint8Array as unknown as ArrayBuffer
        );
      const afterApplyUpdatesCallback = () => {
        if (this._destroyed) {
          return this;
        }
        this.synced = true;
        this.emit('synced', [this]);
      };
      fetchUpdates(this, beforeApplyUpdatesCallback, afterApplyUpdatesCallback);
    });
    /**
     * Timeout in ms untill data is merged and persisted in idb.
     */
    this._storeTimeout = 1000;
    this._storeTimeoutId = null;

    this._storeUpdate = (update: Uint8Array, origin: any) => {
      // NOTE (akishore): Gap in JS implementation - errors in a document update
      // handler funnel up to `doc.transact`, and affect all other handlers.
      try {
        if (this.db && origin !== this) {
          const [updatesStore] = idb.transact(this.db, [updatesStoreName]);
          // lib0's types are overly restrictive; IndexedDB accepts Uint8Array directly
          idb.addAutoKey(
            updatesStore,
            update satisfies Uint8Array as unknown as ArrayBuffer
          );
          if (++this._dbsize >= PREFERRED_TRIM_SIZE) {
            // debounce store call
            if (this._storeTimeoutId !== null) {
              clearTimeout(this._storeTimeoutId);
            }
            this._storeTimeoutId = setTimeout(() => {
              storeState(this, false);
              this._storeTimeoutId = null;
            }, this._storeTimeout);
          }
        }
      } catch (error) {
        console.error('y-indexeddb: error storing update', error);
        this._indexeddbHealthcheckCallback(false);
        captureException(error, {
          tags: {
            origin: origin?.toString(),
            captureLocation: 'y-indexeddb#storeUpdate',
          },
        });
      }
    };
    doc.on('update', this._storeUpdate);
    this.destroy = this.destroy.bind(this);
    doc.on('destroy', this.destroy);
  }

  destroy(): Promise<void> {
    // NOTE (akishore): Gap in JS implementation - errors in a document update
    // handler funnel up to `doc.transact`, and affect all other handlers.
    try {
      this.doc.off('update', this._storeUpdate);
      this.doc.off('destroy', this.destroy);
      this._destroyed = true;
      return this._db.then((db) => {
        db.close();
      });
    } catch (error) {
      console.error('y-indexeddb: error destroying', error);
      captureException(error, {
        tags: {
          captureLocation: 'y-indexeddb#destroy',
        },
      });
      return Promise.resolve(undefined);
    }
  }

  /**
   * Destroys this instance and removes all data from indexeddb.
   *
   * @return {Promise<void>}
   */
  clearData(): Promise<void> {
    return this.destroy().then(() => {
      idb.deleteDB(this.name);
    });
  }

  /**
   * @param {String | number | ArrayBuffer | Date} key
   * @return {Promise<String | number | ArrayBuffer | Date | any>}
   */
  get(
    key: string | number | ArrayBuffer | Date
  ): Promise<string | number | ArrayBuffer | Date | any> {
    return this._db.then((db) => {
      const [custom] = idb.transact(db, [customStoreName], 'readonly');
      return idb.get(custom, key);
    });
  }

  /**
   * @param {String | number | ArrayBuffer | Date} key
   * @param {String | number | ArrayBuffer | Date} value
   * @return {Promise<String | number | ArrayBuffer | Date>}
   */
  set(
    key: string | number | ArrayBuffer | Date,
    value: string | number | ArrayBuffer | Date
  ): Promise<string | number | ArrayBuffer | Date> {
    return this._db.then((db) => {
      const [custom] = idb.transact(db, [customStoreName]);
      return idb.put(custom, value, key);
    });
  }

  /**
   * @param {String | number | ArrayBuffer | Date} key
   * @return {Promise<undefined>}
   */
  del(key: string | number | ArrayBuffer | Date): Promise<void> {
    return this._db.then((db) => {
      const [custom] = idb.transact(db, [customStoreName]);
      return idb.del(custom, key);
    });
  }
}
